/**
 * ExtractionConfigModal - AI 문제 추출 설정 모달
 *
 * 난이도 선택과 매스컨셉 선택을 통해 문제 추출 조건을 설정하는 모달
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import MathName from '../SyllabusV2/MathName';
import { createPortal } from 'react-dom';
import { Modal, Button, MathceptDetailModal } from '../common';
import { Loader2 } from 'lucide-react';
import { fetchMathConcepts } from '../../utils/mathConceptsUtils';
import { isEngineDefault } from '../../utils/aitrackerTimelineCache';

// 난이도별 색상 (선택/미선택)
const DIFFICULTY_RGBA = {
  1: { selected: 'rgba(234, 179, 8, 0.8)',    unselected: 'rgba(253, 224, 71, 0.3)' },   // yellow-500 / yellow-300
  2: { selected: 'rgba(34, 197, 94, 0.8)',    unselected: 'rgba(134, 239, 172, 0.3)' },  // green-500 / green-300
  3: { selected: 'rgba(59, 130, 246, 0.8)',   unselected: 'rgba(147, 197, 253, 0.3)' },  // blue-500 / blue-300
  4: { selected: 'rgba(249, 115, 22, 0.8)',   unselected: 'rgba(253, 186, 116, 0.3)' },  // orange-500 / orange-300
  5: { selected: 'rgba(239, 68, 68, 0.8)',    unselected: 'rgba(252, 165, 165, 0.3)' },  // red-500 / red-300
};

// 개념 타입별 색상
const getConceptTypeColor = (type) => {
  const colors = {
    'DEF': 'rgba(239, 68, 68, 0.5)',
    'AXM': 'rgba(249, 115, 22, 0.5)',
    'IDN': 'rgba(245, 158, 11, 0.5)',
    'THM': 'rgba(34, 197, 94, 0.5)',
    'MTH': 'rgba(6, 182, 212, 0.5)',
    'PRP': 'rgba(59, 130, 246, 0.5)',
    'FOR': 'rgba(139, 92, 246, 0.5)',
    'NTN': 'rgba(236, 72, 153, 0.5)',
  };
  return colors[type] || 'rgba(156, 163, 175, 0.5)';
};

// 수학 타입별 색상
const getMathTypeColor = (type) => {
  const colors = {
    'AL': 'rgba(99, 102, 241, 0.5)',
    'AN': 'rgba(244, 63, 94, 0.5)',
    'GE': 'rgba(16, 185, 129, 0.5)',
    'PS': 'rgba(168, 85, 247, 0.5)',
  };
  return colors[type] || 'rgba(107, 114, 128, 0.5)';
};

// 개념명 파싱: "AL-DEF:집합" → "집합"
const parseConceptName = (text) => {
  if (!text) return '';
  const colonIndex = text.indexOf(':');
  return colonIndex >= 0 ? text.substring(colonIndex + 1) : text;
};

// engineData에서 양자화된 값 디코딩
const decodeQ = (q, scale) => {
  if (q === null || q === undefined) return null;
  if (!scale) return q;
  return q / scale;
};

// 헤더 클릭 정렬 순환: asc → desc → 기본(원래 순서)
const handleTableSortClick = (column, currentSort, setSortFn) => {
  if (currentSort.column === column) {
    if (currentSort.direction === 'asc') {
      setSortFn({ column, direction: 'desc' });
    } else {
      setSortFn({ column: null, direction: null });
    }
  } else {
    setSortFn({ column, direction: 'asc' });
  }
};

// 공통 정렬 함수
const sortConceptRows = (rows, sort) => {
  if (!sort.column || !sort.direction) return rows;
  const multiplier = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    let aVal, bVal;
    switch (sort.column) {
      case 'r':
        aVal = a._r ?? -Infinity;
        bVal = b._r ?? -Infinity;
        break;
      case 'h':
        aVal = a._h ?? -Infinity;
        bVal = b._h ?? -Infinity;
        break;
      case 'st':
        aVal = a._st ?? -Infinity;
        bVal = b._st ?? -Infinity;
        break;
      default:
        return 0;
    }
    if (aVal < bVal) return -1 * multiplier;
    if (aVal > bVal) return 1 * multiplier;
    return 0;
  });
};

function ExtractionConfigModal({ isOpen, onClose, onExtract, isExtracting, engineData }) {
  const [selectedDifficulties, setSelectedDifficulties] = useState(new Set([1, 2, 3, 4, 5]));
  const [selectedConceptIds, setSelectedConceptIds] = useState(new Set());
  const [mathConcepts, setMathConcepts] = useState([]);
  const [isLoadingConcepts, setIsLoadingConcepts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hideDefaults, setHideDefaults] = useState(false); // 초기값(미학습) 개념 숨김 — 추출은 기본 OFF
  const [tableSort, setTableSort] = useState({ column: null, direction: null });
  // r/h/st 범위 필터
  const [rRange, setRRange] = useState([-1, 1]);
  const [hRange, setHRange] = useState([-1, 1]);
  const [stRange, setStRange] = useState([0, 1]);
  const [columnFilterPopup, setColumnFilterPopup] = useState(null);
  const prevBoundsRef = useRef(null);

  // 매스컨셉 상세 모달 상태
  const [showMathceptModal, setShowMathceptModal] = useState(false);
  const [selectedDetailConceptId, setSelectedDetailConceptId] = useState(null);

  // 매스컨셉 로드
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const loadConcepts = async () => {
      setIsLoadingConcepts(true);
      try {
        const concepts = await fetchMathConcepts();
        if (!cancelled) {
          setMathConcepts(concepts);
        }
      } catch (error) {
        console.error('매스컨셉 로드 실패:', error);
      } finally {
        if (!cancelled) {
          setIsLoadingConcepts(false);
        }
      }
    };

    loadConcepts();
    return () => { cancelled = true; };
  }, [isOpen]);

  // engineData에서 concept별 r, h, st 맵 생성
  const engineMap = useMemo(() => {
    if (!engineData?.concept_index) return {};
    const vScale = engineData.v_scale || 10000;
    const idx = engineData.concept_index;
    const r = engineData.r_status || [];
    const h = engineData.h_status || [];
    const s = engineData.stability || [];
    const map = {};
    idx.forEach((cid, i) => {
      map[cid] = {
        r: decodeQ(r[i], vScale),
        h: decodeQ(h[i], vScale),
        st: decodeQ(s[i], vScale),
        // 초기값 판정은 양자화 원본값 기준(표시값 0.00/0.50 비교 금지) — AI 트래커와 동일 규칙
        isDefault: isEngineDefault(r[i], h[i], s[i], vScale),
      };
    });
    return map;
  }, [engineData]);

  // 정렬된 매스컨셉 목록 (r, h, st 병합)
  const sortedConcepts = useMemo(() => {
    return [...mathConcepts].sort((a, b) => {
      if (a.order !== b.order) return (a.order || 0) - (b.order || 0);
      if (a.math_type !== b.math_type) return (a.math_type || '').localeCompare(b.math_type || '');
      if (a.concept_type !== b.concept_type) return (a.concept_type || '').localeCompare(b.concept_type || '');
      return parseConceptName(a.text).localeCompare(parseConceptName(b.text));
    }).map(c => {
      const eng = engineMap[c.id] || {};
      // _unlearned = 엔진 초기값(isDefault) + 엔진 응답에 아예 없는 개념(r/h/st 전무). 둘 다 '학습 기록 없음'이라
      // 숨김 대상을 하나로 묶는다 — isDefault만 걸러내면 가장 기록이 없는 행이 오히려 남아 기준이 뒤섞인다.
      const noEngine = eng.r == null && eng.h == null && eng.st == null;
      return {
        ...c,
        _r: eng.r ?? null, _h: eng.h ?? null, _st: eng.st ?? null,
        _unlearned: eng.isDefault === true || noEngine,
      };
    });
  }, [mathConcepts, engineMap]);

  const defaultConceptCount = useMemo(() => sortedConcepts.filter(c => c._unlearned).length, [sortedConcepts]);

  // 초기값 숨김 + 검색 필터링된 매스컨셉
  const filteredConcepts = useMemo(() => {
    const base = hideDefaults ? sortedConcepts.filter(c => !c._unlearned) : sortedConcepts;
    if (!searchQuery.trim()) return base;
    const query = searchQuery.toLowerCase().trim();
    return base.filter(c => {
      const conceptName = parseConceptName(c.text).toLowerCase();
      const mathType = (c.math_type || '').toLowerCase();
      const conceptType = (c.concept_type || '').toLowerCase();
      const tagsMatch = (c.tags || []).some(tag => tag.toLowerCase().includes(query));
      return conceptName.includes(query) || mathType.includes(query) || conceptType.includes(query) || tagsMatch;
    });
  }, [sortedConcepts, searchQuery, hideDefaults]);

  // r/h/st bounds 계산 (필터 적용 전 데이터 기준)
  const rhstBounds = useMemo(() => {
    const concepts = filteredConcepts.filter(c => c._r != null || c._h != null || c._st != null);
    if (concepts.length === 0) return { r: [-1, 1], h: [-1, 1], st: [0, 1] };
    const rVals = concepts.map(c => c._r).filter(v => v != null);
    const hVals = concepts.map(c => c._h).filter(v => v != null);
    const stVals = concepts.map(c => c._st).filter(v => v != null);
    const bounds = (arr) => arr.length === 0 ? [-1, 1] : [Math.min(...arr), Math.max(...arr)];
    return { r: bounds(rVals), h: bounds(hVals), st: bounds(stVals) };
  }, [filteredConcepts]);

  // 최초 bounds 로드 시 범위 초기화. 초기값 숨김 토글도 리셋 대상 — 숨김 중 좁아진 bounds 안에서 잡은 범위를
  // 그대로 두면 숨김을 해제해도 새로 드러난 개념이 곧바로 범위 필터에 걸려 사라진다(AI 트래커와 같은 규칙).
  const prevHideDefaultsRef = useRef(hideDefaults);
  useEffect(() => {
    const hideChanged = prevHideDefaultsRef.current !== hideDefaults;
    if ((prevBoundsRef.current === null && filteredConcepts.length > 0) || hideChanged) {
      prevHideDefaultsRef.current = hideDefaults;
      prevBoundsRef.current = rhstBounds;
      setRRange(rhstBounds.r);
      setHRange(rhstBounds.h);
      setStRange(rhstBounds.st);
    }
  }, [rhstBounds, filteredConcepts.length, hideDefaults]);

  // 모달 닫힐 때 리셋
  useEffect(() => {
    if (!isOpen) {
      prevBoundsRef.current = null;
      setRRange([-1, 1]);
      setHRange([-1, 1]);
      setStRange([0, 1]);
      setColumnFilterPopup(null);
    }
  }, [isOpen]);

  // 범위 필터 적용
  const rangeFilteredConcepts = useMemo(() => {
    return filteredConcepts.filter(c => {
      if (c._r != null && (c._r < rRange[0] || c._r > rRange[1])) return false;
      if (c._h != null && (c._h < hRange[0] || c._h > hRange[1])) return false;
      if (c._st != null && (c._st < stRange[0] || c._st > stRange[1])) return false;
      return true;
    });
  }, [filteredConcepts, rRange, hRange, stRange]);

  // 컬럼 필터 팝오버 열기
  const openColumnFilter = useCallback((column, anchorEl) => {
    const rect = anchorEl.getBoundingClientRect();
    setColumnFilterPopup(prev =>
      prev && prev.column === column ? null : { column, rect }
    );
  }, []);

  // 범위 필터 활성 여부
  const isRangeFiltered = useCallback((column) => {
    const bounds = rhstBounds[column];
    const range = column === 'r' ? rRange : column === 'h' ? hRange : stRange;
    return range[0] > bounds[0] || range[1] < bounds[1];
  }, [rhstBounds, rRange, hRange, stRange]);

  // 정렬 적용된 매스컨셉
  const displayConcepts = useMemo(() => sortConceptRows(rangeFilteredConcepts, tableSort), [rangeFilteredConcepts, tableSort]);

  // 난이도 토글
  const toggleDifficulty = useCallback((level) => {
    setSelectedDifficulties(prev => {
      const next = new Set(prev);
      if (next.has(level)) {
        next.delete(level);
      } else {
        next.add(level);
      }
      return next;
    });
  }, []);

  // 난이도 전체 선택/해제
  const toggleAllDifficulties = useCallback(() => {
    setSelectedDifficulties(prev => {
      if (prev.size === 5) return new Set();
      return new Set([1, 2, 3, 4, 5]);
    });
  }, []);

  // 매스컨셉 토글
  const toggleConcept = useCallback((conceptId) => {
    setSelectedConceptIds(prev => {
      const next = new Set(prev);
      if (next.has(conceptId)) {
        next.delete(conceptId);
      } else {
        if (next.size >= 200) {
          alert('최대 200개까지 선택할 수 있습니다.');
          return prev;
        }
        next.add(conceptId);
      }
      return next;
    });
  }, []);

  // 검색 결과 전체 선택/해제
  const hasSearch = searchQuery.trim().length > 0;
  const filteredIds = useMemo(() => new Set(filteredConcepts.map(c => c.id)), [filteredConcepts]);
  const allFilteredSelected = hasSearch && filteredIds.size > 0 && [...filteredIds].every(id => selectedConceptIds.has(id));

  const toggleAllConcepts = useCallback(() => {
    if (!hasSearch) return;
    setSelectedConceptIds(prev => {
      // 검색된 항목이 모두 선택된 상태면 검색된 것만 해제
      const allSelected = [...filteredIds].every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        filteredIds.forEach(id => next.delete(id));
        return next;
      }
      // 아니면 검색된 것 추가
      const next = new Set(prev);
      filteredIds.forEach(id => next.add(id));
      if (next.size > 200) {
        alert('최대 200개까지 선택할 수 있습니다. 검색으로 범위를 좁혀주세요.');
        return prev;
      }
      return next;
    });
  }, [hasSearch, filteredIds]);

  // 추출 실행
  const handleExtract = useCallback(() => {
    if (selectedDifficulties.size === 0) return;
    onExtract({
      difficulty: Array.from(selectedDifficulties).sort(),
      userSelectedCids: Array.from(selectedConceptIds),
    });
  }, [selectedDifficulties, selectedConceptIds, onExtract]);

  // 뱃지 클릭 → 상세 모달
  const handleBadgeClick = useCallback((e, conceptId) => {
    e.stopPropagation();
    setSelectedDetailConceptId(conceptId);
    setShowMathceptModal(true);
  }, []);

  const allDifficultiesSelected = selectedDifficulties.size === 5;

  // r, h, st 값 포맷
  const fmtVal = (v) => v != null ? (Math.trunc(v * 100) / 100).toFixed(2) : '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="AI 문제 추출 설정"
      size="lg"
      fixedHeight
      maxHeight="85vh"
      bodyClassName="overflow-hidden flex flex-col"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isExtracting}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleExtract}
            disabled={selectedDifficulties.size === 0 || isExtracting}
            loading={isExtracting}
          >
            추출하기
          </Button>
        </>
      }
    >
      {/* 난이도 선택 영역 */}
      <div className="mb-4 shrink-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-700">난이도</span>
          <button
            type="button"
            className="text-xs text-gray-500 hover:text-gray-700 underline"
            onClick={toggleAllDifficulties}
          >
            {allDifficultiesSelected ? '전체 해제' : '전체 선택'}
          </button>
        </div>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map(level => {
            const isSelected = selectedDifficulties.has(level);
            return (
              <button
                key={level}
                type="button"
                className="flex-1 py-1.5 rounded-full text-sm font-bold text-white border border-transparent transition-opacity"
                style={{
                  backgroundColor: isSelected ? DIFFICULTY_RGBA[level].selected : DIFFICULTY_RGBA[level].unselected,
                }}
                onClick={() => toggleDifficulty(level)}
              >
                Lv.{level}
              </button>
            );
          })}
        </div>
      </div>

      {/* 매스컨셉 선택 영역 */}
      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between mb-2 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-700">수학 개념 선택</span>
            {selectedConceptIds.size > 0 && (
              <span className="text-xs text-primary font-medium">
                ({selectedConceptIds.size}개 선택)
              </span>
            )}
          </div>
          <button
            type="button"
            className={`text-xs underline ${hasSearch ? 'text-gray-500 hover:text-gray-700' : 'text-gray-300 cursor-not-allowed'}`}
            onClick={toggleAllConcepts}
            disabled={!hasSearch}
          >
            {allFilteredSelected ? '검색 결과 해제' : '검색 결과 선택'}
          </button>
        </div>

        {/* 검색 입력란 */}
        <div className="relative mb-2 shrink-0">
          <input
            type="text"
            placeholder="개념명, 수학 타입, 개념 타입으로 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>

        {/* 안내 문구 + 초기값 숨김 + 검색 결과 수 */}
        <div className="flex items-center justify-between gap-2 mb-2 shrink-0">
          <p className="text-xs text-gray-400">
            선택하지 않으면 알고리즘이 자동으로 개념을 선정합니다
          </p>
          <div className="flex shrink-0 items-center gap-3">
            {/* AI 트래커 '초기값 숨김'과 동일 판정(isEngineDefault). 추출은 미학습 개념 선택도 정당해 기본 OFF. */}
            <label
              className="flex shrink-0 items-center gap-1 cursor-pointer"
              title={'엔진 초기값(직접·연관·안정도가 baseline 그대로 = 학습 기록 미반영)인 개념을 목록에서 숨깁니다.'}
            >
              <input
                type="checkbox"
                checked={hideDefaults}
                onChange={(e) => setHideDefaults(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-xs text-gray-500">
                초기값 숨김{hideDefaults && defaultConceptCount > 0 ? ` ${defaultConceptCount}` : ''}
              </span>
            </label>
            {hasSearch && (
              <span className="text-xs text-gray-500">
                검색 결과 <span className="font-semibold text-gray-700">{displayConcepts.length}</span>개
              </span>
            )}
          </div>
        </div>

        {/* 매스컨셉 테이블 */}
        <div className="flex-1 min-h-0 overflow-auto rounded border border-gray-200 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300" style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db transparent' }}>
          {isLoadingConcepts ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
              <span className="text-sm text-gray-500">개념 목록을 불러오는 중...</span>
            </div>
          ) : (
            <table className="w-full border-collapse text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="bg-blue-50">
                  <th className="w-8 border-b border-gray-200 px-2 py-1.5 text-center">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAllConcepts}
                      disabled={!hasSearch}
                      className="rounded disabled:opacity-30"
                    />
                  </th>
                  <th className="border-b border-gray-200 px-2 py-1.5 text-left font-semibold text-gray-700">수학개념</th>
                  {['r', 'h', 'st'].map(col => (
                    <th
                      key={col}
                      className="cursor-pointer border-b border-gray-200 px-2 py-1.5 text-right font-semibold text-gray-700 hover:bg-blue-100 select-none whitespace-nowrap"
                      onClick={(e) => openColumnFilter(col, e.currentTarget)}
                    >
                      {col} {tableSort.column === col ? (tableSort.direction === 'desc' ? '▼' : '▲') : '↕'}
                      {isRangeFiltered(col) && <span className="ml-0.5 inline-block h-1.5 w-1.5 rounded-full bg-primary align-middle" />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayConcepts.map(concept => (
                  <tr
                    key={concept.id}
                    className={`cursor-pointer border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      selectedConceptIds.has(concept.id) ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => toggleConcept(concept.id)}
                  >
                    <td className="px-2 py-1 text-center">
                      <input
                        type="checkbox"
                        checked={selectedConceptIds.has(concept.id)}
                        onChange={() => toggleConcept(concept.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-1 text-xs">
                        {concept.order != null && (
                          <span
                            className="inline-flex items-center justify-center shrink-0 rounded-md px-1 text-[9px] font-semibold text-gray-700 cursor-pointer hover:opacity-80"
                            style={{ backgroundColor: 'rgba(156, 163, 175, 0.3)', minWidth: '24px', height: '16px' }}
                            onClick={(e) => handleBadgeClick(e, concept.id)}
                            title="클릭하여 상세 정보 보기"
                          >
                            {concept.order}
                          </span>
                        )}
                        {concept.math_type && (
                          <span
                            className="inline-flex items-center justify-center shrink-0 rounded-md px-1 text-[9px] font-semibold text-gray-700 cursor-pointer hover:opacity-80"
                            style={{ backgroundColor: getMathTypeColor(concept.math_type), minWidth: '24px', height: '16px' }}
                            onClick={(e) => handleBadgeClick(e, concept.id)}
                            title="클릭하여 상세 정보 보기"
                          >
                            {concept.math_type}
                          </span>
                        )}
                        {concept.concept_type && (
                          <span
                            className="inline-flex items-center justify-center shrink-0 rounded-md px-1 text-[8px] font-semibold text-gray-800 cursor-pointer hover:opacity-80"
                            style={{ backgroundColor: getConceptTypeColor(concept.concept_type), minWidth: '24px', height: '16px' }}
                            onClick={(e) => handleBadgeClick(e, concept.id)}
                            title="클릭하여 상세 정보 보기"
                          >
                            {concept.concept_type}
                          </span>
                        )}
                        <span><MathName text={parseConceptName(concept.text)} compact /></span>
                        {concept.tags && concept.tags.length > 0 && (
                          <span className="inline-flex items-center gap-1 ml-auto shrink-0">
                            {concept.tags.slice(0, 3).map((tag, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center rounded px-1 text-[9px] font-medium"
                                style={{ backgroundColor: '#e0f2fe', color: '#0369a1', height: '14px', lineHeight: '14px' }}
                              >
                                {tag}
                              </span>
                            ))}
                            {concept.tags.length > 3 && (
                              <span className="text-[9px] text-gray-400">+{concept.tags.length - 3}</span>
                            )}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={`px-2 py-1 text-right tabular-nums ${concept._r > 0 ? 'text-green-600' : concept._r < 0 ? 'text-red-500' : 'text-gray-600'}`} style={{ fontWeight: Math.round(400 + Math.abs(concept._r ?? 0) * 500) }}>{fmtVal(concept._r)}</td>
                    <td className={`px-2 py-1 text-right tabular-nums ${concept._h > 0 ? 'text-green-600' : concept._h < 0 ? 'text-red-500' : 'text-gray-600'}`} style={{ fontWeight: Math.round(400 + Math.abs(concept._h ?? 0) * 500) }}>{fmtVal(concept._h)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-gray-600" style={{ fontWeight: Math.round(400 + (concept._st ?? 0) * 500) }}>{fmtVal(concept._st)}</td>
                  </tr>
                ))}
                {displayConcepts.length === 0 && !isLoadingConcepts && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">
                      {searchQuery
                        ? '검색 결과가 없습니다'
                        : hideDefaults && defaultConceptCount > 0
                          ? '학습 기록이 반영된 개념 없음 (초기값 숨김 해제 시 표시)'
                          : '매스컨셉이 없습니다'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 매스컨셉 상세 정보 모달 */}
      <MathceptDetailModal
        isOpen={showMathceptModal}
        onClose={() => {
          setShowMathceptModal(false);
          setSelectedDetailConceptId(null);
        }}
        conceptIds={selectedDetailConceptId ? [selectedDetailConceptId] : []}
        title="매스컨셉 상세 정보"
        useLoader={true}
        useBearerPrefix={true}
      />

      {/* 컬럼 필터 팝오버 */}
      {columnFilterPopup && createPortal(
        (() => {
          const { column, rect } = columnFilterPopup;
          const bounds = rhstBounds[column];
          const range = column === 'r' ? rRange : column === 'h' ? hRange : stRange;
          const setRange = column === 'r' ? setRRange : column === 'h' ? setHRange : setStRange;
          const bMin = bounds[0];
          const bMax = bounds[1];
          const bRange = bMax - bMin || 1;
          const clampedMin = Math.max(bMin, Math.min(bMax, range[0]));
          const clampedMax = Math.max(bMin, Math.min(bMax, range[1]));

          return (
            <div
              className="fixed inset-0"
              style={{ zIndex: 9999990 }}
              onClick={() => setColumnFilterPopup(null)}
            >
              <div
                className="absolute z-50 w-52 rounded-lg border bg-white p-3 shadow-lg"
                style={{
                  top: rect.bottom + 4,
                  left: Math.max(8, Math.min(rect.left + rect.width / 2 - 104, window.innerWidth - 208 - 8))
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* 정렬 */}
                <div className="mb-2">
                  <div className="mb-1 text-[10px] font-semibold text-gray-500">{column} 정렬</div>
                  <div className="flex gap-1">
                    {[
                      { dir: 'asc', label: '▲ 오름차순' },
                      { dir: 'desc', label: '▼ 내림차순' },
                    ].map(({ dir, label }) => (
                      <button
                        key={dir}
                        className={`flex-1 rounded px-2 py-1 text-[10px] font-medium transition-colors ${
                          tableSort.column === column && tableSort.direction === dir
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        onClick={() => {
                          if (tableSort.column === column && tableSort.direction === dir) {
                            setTableSort({ column: null, direction: null });
                          } else {
                            setTableSort({ column, direction: dir });
                          }
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 범위 슬라이더 */}
                <div>
                  <div className="mb-1 text-[10px] font-semibold text-gray-500">{column} 범위</div>
                  <div className="relative h-8">
                    <div className="absolute top-2 left-0 right-0 h-1 rounded bg-gray-200" />
                    <div
                      className="absolute top-2 h-1 rounded bg-primary"
                      style={{
                        left: `${((clampedMin - bMin) / bRange) * 100}%`,
                        right: `${((bMax - clampedMax) / bRange) * 100}%`
                      }}
                    />
                    <input
                      type="range"
                      min={bMin}
                      max={bMax}
                      step={0.01}
                      value={clampedMin}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setRange([Math.min(v, range[1] - 0.01), range[1]]);
                      }}
                      className="absolute top-0 left-0 z-20 w-full h-5 cursor-pointer appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer"
                    />
                    <input
                      type="range"
                      min={bMin}
                      max={bMax}
                      step={0.01}
                      value={clampedMax}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setRange([range[0], Math.max(v, range[0] + 0.01)]);
                      }}
                      className="absolute top-0 left-0 z-10 w-full h-5 cursor-pointer appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer"
                    />
                    <div className="absolute bottom-0 left-0 right-0 flex justify-between">
                      <span className="text-[9px] text-gray-400 tabular-nums">{clampedMin.toFixed(2)}</span>
                      <span className="text-[9px] text-gray-400 tabular-nums">{clampedMax.toFixed(2)}</span>
                    </div>
                  </div>
                  <button
                    className="mt-1.5 w-full rounded bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500 hover:bg-gray-200"
                    onClick={() => setRange([bMin, bMax])}
                  >
                    범위 초기화
                  </button>
                </div>
              </div>
            </div>
          );
        })(),
        document.body
      )}
    </Modal>
  );
}

export default ExtractionConfigModal;
